import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, label, btnPrimary, btnSecondary } from '../styles/theme.js'

const formRow = css`
  margin-bottom: ${space.md};
`

const formActions = css`
  display: flex;
  gap: ${space.sm};
  justify-content: flex-end;
  margin-top: ${space.lg};
`

const typeSelect = css`
  ${input};
  appearance: auto;
`

const tagRow = css`
  display: flex;
  gap: ${space.xs};
  flex-wrap: wrap;
  margin-top: ${space.xs};
`

const tagChip = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${colors.bgSidebar};
  font-size: ${font.size.xs};
  border: 1px solid ${colors.border};
`

const removeBtn = css`
  cursor: pointer;
  color: ${colors.textMuted};
  font-weight: ${font.weight.bold};
  &:hover { color: ${colors.critical}; }
`

const addValueRow = css`
  display: flex;
  gap: ${space.xs};
  margin-top: ${space.xs};
`

const smallInput = css`
  ${input};
  flex: 1;
  font-size: ${font.size.sm};
  padding: ${space.xs} ${space.sm};
`

const smallBtn = css`
  ${btnSecondary};
  font-size: ${font.size.xs};
  padding: ${space.xs} ${space.sm};
`

/**
 * DetailEditor component for creating/editing pack details.
 *
 * attrs:
 *   detail: existing detail object (null for create)
 *   onSave: (data) => Promise
 *   onCancel: () => void
 */
export function DetailEditor() {
  let form = { name: '', description: '', type: 'enum', values: [], rangeMin: '', rangeMax: '' }
  let newValue = ''
  let saving = false
  let error = null

  function initForm(detail) {
    if (detail) {
      const labels = (detail.options || []).map(o => typeof o === 'string' ? o : o.label)
      form = {
        name: detail.name,
        description: detail.description || '',
        type: detail.type,
        options: [...labels],
        rangeMin: detail.range_min != null ? String(detail.range_min) : '',
        rangeMax: detail.range_max != null ? String(detail.range_max) : '',
      }
    } else {
      form = { name: '', description: '', type: 'enum', options: [], rangeMin: '', rangeMax: '' }
    }
  }

  function addValue() {
    const v = newValue.trim()
    if (v && !form.options.includes(v)) {
      form.options.push(v)
      newValue = ''
    }
  }

  function removeValue(idx) {
    form.options.splice(idx, 1)
  }

  async function handleSubmit(e, onSave) {
    e.preventDefault()
    if (!form.name.trim()) return
    saving = true
    error = null
    try {
      const data = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        type: form.type,
      }
      if (form.type === 'enum') {
        data.options = form.options
      } else if (form.type === 'range') {
        data.rangeMin = form.rangeMin !== '' ? Number(form.rangeMin) : undefined
        data.rangeMax = form.rangeMax !== '' ? Number(form.rangeMax) : undefined
      }
      await onSave(data)
    } catch (e) {
      error = e.message
    }
    saving = false
    m.redraw()
  }

  return {
    oninit(vnode) {
      initForm(vnode.attrs.detail)
    },
    view(vnode) {
      const { onSave, onCancel, detail } = vnode.attrs

      return m('form', {
        onsubmit: (e) => handleSubmit(e, onSave),
      }, [
        error ? m('div', {
          style: `color: ${colors.critical}; margin-bottom: ${space.sm}; font-size: ${font.size.sm};`,
        }, error) : null,

        m('div', { class: formRow }, [
          m('label', { class: label }, 'Name'),
          m('input', {
            class: input,
            value: form.name,
            oninput: (e) => { form.name = e.target.value },
            placeholder: 'e.g., Income Level',
          }),
        ]),

        m('div', { class: formRow }, [
          m('label', { class: label }, 'Description (optional)'),
          m('input', {
            class: input,
            value: form.description,
            oninput: (e) => { form.description = e.target.value },
            placeholder: 'What does this detail represent?',
          }),
        ]),

        m('div', { class: formRow }, [
          m('label', { class: label }, 'Type'),
          m('select', {
            class: typeSelect,
            value: form.type,
            onchange: (e) => { form.type = e.target.value },
          }, [
            m('option', { value: 'enum' }, 'List'),
            m('option', { value: 'boolean' }, 'Boolean (true/false)'),
          ]),
        ]),

        form.type === 'enum' ? m('div', { class: formRow }, [
          m('label', { class: label }, 'Values'),
          form.options.length > 0 ? m('div', { class: tagRow },
            form.options.map((v, i) =>
              m('span', { class: tagChip, key: v }, [
                v,
                m('span', {
                  class: removeBtn,
                  onclick: () => removeValue(i),
                }, '\u00d7'),
              ])
            )
          ) : null,
          m('div', { class: addValueRow }, [
            m('input', {
              class: smallInput,
              value: newValue,
              oninput: (e) => { newValue = e.target.value },
              placeholder: 'Add a value...',
              onkeydown: (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addValue()
                }
              },
            }),
            m('button', {
              class: smallBtn,
              type: 'button',
              onclick: addValue,
            }, 'Add'),
          ]),
        ]) : null,

        form.type === 'range' ? m('div', { class: formRow }, [
          m('label', { class: label }, 'Range'),
          m('div', { style: `display: flex; gap: ${space.sm}; align-items: center;` }, [
            m('input', {
              class: smallInput,
              type: 'number',
              value: form.rangeMin,
              oninput: (e) => { form.rangeMin = e.target.value },
              placeholder: 'Min',
              style: 'flex: 1;',
            }),
            m('span', { style: `color: ${colors.textMuted};` }, 'to'),
            m('input', {
              class: smallInput,
              type: 'number',
              value: form.rangeMax,
              oninput: (e) => { form.rangeMax = e.target.value },
              placeholder: 'Max',
              style: 'flex: 1;',
            }),
          ]),
        ]) : null,

        m('div', { class: formActions }, [
          m('button', {
            class: btnSecondary,
            type: 'button',
            onclick: onCancel,
          }, 'Cancel'),
          m('button', {
            class: btnPrimary,
            type: 'submit',
            disabled: saving || !form.name.trim(),
          }, saving ? 'Saving...' : detail ? 'Update' : 'Create'),
        ]),
      ])
    },
  }
}
